import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, notification, Card, Upload } from 'antd';
import { useMessageAttachments } from '../hooks/useMessageAttachments';
import { useMessages } from '../hooks/useMessages';
import RecipientDropDown from './RecipientDropDown';
import { useSelector } from "react-redux";
import { PREDEFINED_GROUPS } from "../../../../constants/messageConstants";
import { useSignature } from '../hooks/useSignature';
import SignatureModal from './SignatureModal';
import ReactQuill from "react-quill";
import CustomModal from "components/Common/CustomModal";
import CustomButton from "components/Common/ui/CustomButton";

interface ComposeEmailProps {
  onBack: () => void;
}

const ComposeEmail: React.FC<ComposeEmailProps> = ({ onBack }) => {
  const {
    attachments,
    uploading,
    error: attachmentError,
    upload,
    remove,
    clear,
    setError: setAttachmentError,
  } = useMessageAttachments();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { sendMessage } = useMessages('inbox');
  const { t } = useTranslation();
  const maxSizeMB = process.env.REACT_APP_MAX_FILE_SIZE_MB;
  const userRole = useSelector((state: any) => state.authSlice.user?.role);
  // Dynamically inject available groups based on role
  const availableGroups = PREDEFINED_GROUPS(t)[userRole as keyof typeof PREDEFINED_GROUPS] ?? [];

  // Signature logic
  const { signature, fetchSignature } = useSignature();
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState('');
  // Fetch signature on mount
  React.useEffect(() => { fetchSignature(); }, [fetchSignature]);

  // When signature loads, update the signatureHtml state and ensure only one signature in the body
  useEffect(() => {
    if (signature) {
      const rawHtml = signature.html;
      const inner = rawHtml.replace(/^<div id=["']signature["'][^>]*>([\s\S]*)<\/div>$/i, '$1');
      setSignatureHtml(inner);

      const wrapper = `<div id="signature">${inner}</div>`;
      const currentBody = form.getFieldValue('body') || '';

      // Strip any existing <div id="signature">...</div>
      const bodyWithoutOldSignature = currentBody.replace(/<div id=["']signature["'][^>]*>[\s\S]*?<\/div>/gi, '').trim();

      // Update the editor with the new body and signature
      const updatedBody = `${bodyWithoutOldSignature}\n${wrapper}`;
      form.setFieldsValue({ body: updatedBody });
    }
  }, [signature]);

  const handleSend = async (values: { recipientIds: string[]; subject: string; body: string }) => {
    setLoading(true);
    try {
      await sendMessage({ ...values, attachments });
      notification.success({
        message: t("email_sent"),
        description: t("email_sent_success"),
      });
      clear();
      form.resetFields();
      onBack();
    } catch (error: any) {
      notification.error({
        message: t("error_sending_email"),
        description: error.message || t("email_send_error"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await form.submit();
  };

  return (
    <CustomModal title={t("compose_email")} isOpen={true} onClose={onBack} onSave={handleSave} saveText={t("send")} >
      <div>


        <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'right' }}>
          <CustomButton variant="light" onClick={() => setSignatureModalVisible(true)}>
            {t('edit_signature')}
          </CustomButton>
        </div>
        <div>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSend}
            className="row"
          >
            {/* Recipients */}
            <Form.Item
              name="recipientIds"
              label={t("recipients")}
              rules={[
                { required: true, message: t("select_at_least_one_recipient") },
              ]}
              className="col-md-12"
            >
              <RecipientDropDown
                value={form.getFieldValue('recipientIds')}
                onChange={(val) => form.setFieldsValue({ recipientIds: val })}
                predefinedGroups={availableGroups}
              />
            </Form.Item>

            {/* Subject */}
            <Form.Item
              name="subject"
              label={t("message_subject")}
              rules={[
                { required: true, message: t("enter_the_subject") },
              ]}
              className="col-md-12"
            >
              <Input placeholder={t("enter_the_subject")} />
            </Form.Item>

            {/* Body */}
            <Form.Item
              name="body"
              label={t("body")}
              rules={[
                { required: true, message: t("enter_your_message") },
              ]}
              className="col-md-12"
            >
              <ReactQuill
                value={form.getFieldValue('body')}
                onChange={val => form.setFieldsValue({ body: val })}
                theme="snow"
                // style={{ minHeight: 200 }}
                placeholder={t("enter_your_message")}
              />
            </Form.Item>

            {/* Attachments */}
            <Form.Item label={t('attachments')} className="col-md-12">
              <Upload.Dragger
                multiple
                customRequest={({ file, onSuccess }) => {
                  upload(file as File);
                  if (onSuccess) setTimeout(() => onSuccess('ok'), 0);
                }}
                showUploadList={false}
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                disabled={uploading}
              >
                <p className="ant-upload-drag-icon">
                  <i className="bi bi-upload" />
                </p>
                <p className="ant-upload-text">{t('drag_drop_or_click_to_upload') || 'Drag & drop or click to upload files (PDF, DOCX, JPG, PNG)'}</p>
                <p className="ant-upload-hint">{t('max_file_size', { maxSizeMB }) || `Max file size: ${maxSizeMB}MB. Allowed: PDF, DOCX, JPG, PNG.`}</p>
              </Upload.Dragger>
              {attachmentError && <div style={{ color: 'red', marginTop: 8 }}>{attachmentError}</div>}
              {/* Show selected files */}
              {attachments.length > 0 && (
                <ul style={{ marginTop: 12 }}>
                  {attachments.map(att => (
                    <li key={att.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>
                        <i className="bi bi-paperclip" />
                        <a href={att.url} target="_blank" rel="noopener noreferrer">{att.name}</a> <small>({att.mimetype})</small>
                      </span>
                      <Button size="small" danger onClick={() => remove(att.name)}>{t('remove') || 'Remove'}</Button>
                    </li>
                  ))}
                </ul>
              )}
            </Form.Item>

          </Form>
          <SignatureModal
            visible={signatureModalVisible}
            onClose={() => setSignatureModalVisible(false)}
            onSignatureSaved={fetchSignature}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default ComposeEmail;
